/**
 * Patch 35.1b — Rendered Image Preview
 *
 * Fetches a short-lived signed URL for the primary media file attached
 * to a rendered asset and displays it inline. Handles four states the
 * founder must be able to tell apart at a glance:
 *
 *   1. LOADING        — signed URL fetch is in flight.
 *   2. UNAVAILABLE    — the asset has no media attached (upstream bug or
 *                       still-in-flight render).
 *   3. FAILED         — the ``<img>`` DOM element failed to load the
 *                       fetched URL (expired signature, network, CDN 4xx).
 *   4. OK             — the image is on screen.
 *
 * The component NEVER surfaces storage keys, checksums, provider names,
 * cost figures, or any other founder/admin-only telemetry. Even the
 * signed URL string is only rendered inside ``<img src>`` — never in
 * copyable text — so screen-share leaks are minimized.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AlertCircle, ImageIcon, Loader2, RefreshCcw } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";

import { Button } from "@/shared/ui/button";
import { fetchSignedMediaUrl } from "@/entities/ai-studio/api";

interface Props {
  workspaceId: string;
  assetId: string;
  /** Alt-text hint the founder can override — falls back to the
   * canonical content_type for a11y. */
  altText?: string;
  /** ``max-h-*`` Tailwind class or similar sizing hook. */
  className?: string;
}

export function RenderedImagePreview({
  workspaceId,
  assetId,
  altText,
  className,
}: Props): JSX.Element {
  const qc = useQueryClient();
  const [imgFailed, setImgFailed] = useState(false);
  const q = useQuery({
    queryKey: ["media-engine-signed-url", workspaceId, assetId],
    queryFn: () => fetchSignedMediaUrl(workspaceId, assetId),
    enabled: !!workspaceId && !!assetId,
    // Signed URLs are short-lived; refetch a few seconds before expiry.
    staleTime: 4 * 60 * 1000,
  });
  const refresh = useMutation({
    mutationFn: async () => {
      setImgFailed(false);
      await qc.invalidateQueries({
        queryKey: ["media-engine-signed-url", workspaceId, assetId],
      });
    },
    onSuccess: () => {
      toast.success("Preview refreshed");
    },
  });

  if (q.isLoading) {
    return (
      <div
        data-testid="rendered-image-loading"
        className={`flex items-center justify-center rounded-md border border-border/50 bg-secondary/30 p-8 ${className ?? ""}`}
      >
        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
        <span className="text-xs text-muted-foreground">Loading preview…</span>
      </div>
    );
  }

  if (q.isError) {
    const anyErr = q.error as unknown as { status?: number };
    const notFound = anyErr?.status === 404;
    return (
      <div
        data-testid={notFound ? "rendered-image-unavailable" : "rendered-image-error"}
        className={`flex flex-col items-center justify-center gap-2 rounded-md border border-dashed border-border/60 bg-background/60 p-6 text-center text-xs ${className ?? ""}`}
      >
        <AlertCircle className="h-4 w-4 text-muted-foreground" />
        <span className="text-muted-foreground">
          {notFound
            ? "No rendered image attached yet."
            : "Could not load preview. Try refreshing."}
        </span>
        <Button
          data-testid="rendered-image-retry"
          size="sm"
          variant="outline"
          onClick={() => refresh.mutate()}
        >
          <RefreshCcw className="mr-1 h-3 w-3" /> Retry
        </Button>
      </div>
    );
  }

  const signed = q.data;
  if (!signed || !signed.url) {
    return (
      <div
        data-testid="rendered-image-unavailable"
        className={`flex items-center justify-center rounded-md border border-dashed border-border/60 bg-background/60 p-6 text-center text-xs text-muted-foreground ${className ?? ""}`}
      >
        <ImageIcon className="mr-2 h-4 w-4" /> No preview available.
      </div>
    );
  }

  if (imgFailed) {
    return (
      <div
        data-testid="rendered-image-expired"
        className={`flex flex-col items-center justify-center gap-2 rounded-md border border-dashed border-border/60 bg-background/60 p-6 text-center text-xs ${className ?? ""}`}
      >
        <AlertCircle className="h-4 w-4 text-amber-500" />
        <span className="text-muted-foreground">
          Preview link expired or unreachable.
        </span>
        <Button
          data-testid="rendered-image-retry"
          size="sm"
          variant="outline"
          onClick={() => refresh.mutate()}
        >
          <RefreshCcw className="mr-1 h-3 w-3" /> Refresh preview
        </Button>
      </div>
    );
  }

  return (
    <div
      data-testid="rendered-image-preview"
      className={`overflow-hidden rounded-md border border-border/60 bg-background ${className ?? ""}`}
    >
      <img
        data-testid="rendered-image-preview-img"
        src={signed.url}
        alt={altText ?? "Rendered campaign asset preview"}
        className="mx-auto max-h-96 w-full object-contain"
        loading="lazy"
        onError={() => setImgFailed(true)}
      />
      <div
        data-testid="rendered-image-preview-meta"
        className="flex items-center justify-between border-t border-border/40 bg-secondary/20 px-3 py-1.5 text-[10px] uppercase tracking-widest text-muted-foreground"
      >
        <span>{signed.mime_type}</span>
        {signed.width && signed.height ? (
          <span>
            {signed.width} × {signed.height}
          </span>
        ) : null}
      </div>
    </div>
  );
}
